import { AnnonymousUser, ResetPasswordCredentials } from '../interfaces';

// import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { logout } from '../store/authSlice';

const baseUrl = import.meta.env.VITE_BASEURL as string;
const tokenCookieName = 'accessToken';
const accessEmail = 'email';
const refreshTokenCookieName = 'refreshToken';

// Helper to set a cookie
const setCookie = (name: string, value: string, days = 1) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; Secure`;
};

const handleAuthApiResponse = async (response: Response): Promise<object> => {
  const status = response?.status;
  if (status === 401) {
    console.warn('Unauthorized or Expired Session');
    toast('Unauthorized or Expired Session');
    logout();
  } else if (status === 400) {
    const errorBody = await response.json();
    if (errorBody?.error) {
      toast.error(errorBody.error);
      console.warn('Bad Request:', errorBody.error || 'Invalid request.');
    }
  } else if (status === 403) {
    console.warn(
      "Forbidden: You don't have permission to access this resource."
    );
    toast("Forbidden: You don't have permission to access this resource.");
  } else if (status === 500) {
    console.warn('Server Error: Please try again later.');
    toast('Server Error: Please try again later.');
  } else if (!response.ok) {
    console.warn('Internal Server Error: Please contact support.');
    toast('Internal Server Error: Please contact support.');
  }
  return {};
};

// Helper to get a cookie by name
const getCookie = (name: string) => {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))
    ?.split('=')[1];
};

// Helper to delete a cookie
const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

class AuthService {
  // Helper to set the access token in a cookie
  public static setAccessToken(token: string) {
    setCookie(tokenCookieName, token);
  }

  // Helper to set the refresh token in a cookie
  public static setRefreshToken(token: string) {
    setCookie(refreshTokenCookieName, token);
  }

  public static setAccessEmail(email: string) {
    setCookie(accessEmail, email);
  }

  // Helper to get the access token from cookies
  public static getAccessToken() {
    return getCookie(tokenCookieName);
  }

  public static getRefreshToken() {
    return getCookie(refreshTokenCookieName);
  }

  // Helper to delete the access token cookie
  public static deleteAccessToken() {
    deleteCookie(tokenCookieName);
  }
  public static deleteRefreshToken() {
    deleteCookie(refreshTokenCookieName);
  }
  public static deleteAccessEmail() {
    deleteCookie(accessEmail);
  }

  // Helper to get authorization headers
  public static getAuthHeaders() {
    const token = AuthService.getAccessToken();

    return {
      'Content-Type': 'application/json',
      'x-store-id': localStorage.getItem('storeId')
        ? localStorage.getItem('storeId')!
        : '',
      'x-guest-session-id': localStorage.getItem('guestSessionID') || '',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Login
  static async login(userName: string, password: string) {
    const comapnyId = localStorage.getItem('companyId');
    const response = await fetch(`${baseUrl}auth/login`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({ userName, password, companyId: comapnyId }),
    });
    const data = await response.json();
    if (data.data) {
      if (data.data.accessToken) {
        AuthService.setRefreshToken(data.data.refreshToken);
        AuthService.setAccessToken(data.data.accessToken);
        AuthService.setAccessEmail(data.data.email);
      }
      return data.data;
    }
    return data;
  }

  // Login
  static async forgotPassword(email: string) {
    const companyId = localStorage.getItem('companyId')
      ? localStorage.getItem('companyId')
      : null;
    const response = await fetch(`${baseUrl}auth/storefront/forgot-password`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({ email, companyId: companyId }),
    });
    if (!response.ok) {
      return await handleAuthApiResponse(response);
    }
    const data = await response.json();
    return data;
  }

  // Token Reset password
  static async tokenResetPassword({
    email,
    password,
    token,
    confirmPassword,
  }: ResetPasswordCredentials) {
    const companyId = localStorage.getItem('companyId')
      ? localStorage.getItem('companyId')
      : null;
    const response = await fetch(`${baseUrl}auth/storefront/reset-password`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({
        email,
        password,
        token,
        confirmPassword,
        companyId,
      }),
    });
    if (!response.ok) {
      return await handleAuthApiResponse(response);
    }
    return response.json();
  }

  // Logout
  static async logout() {
    await fetch(`${baseUrl}auth/logout`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
    });
    AuthService.deleteAccessToken();
    AuthService.deleteRefreshToken();
    AuthService.deleteAccessEmail();
    return null;
  }

  // Get user info
  static async getUserInfo() {
    const accessToken = AuthService.getAccessToken();
    if (!accessToken) {
      return {};
    }
    const response = await fetch(`${baseUrl}auth/user-info`, {
      method: 'GET',
      headers: AuthService.getAuthHeaders(),
    });

    if (!response.ok) {
      return await handleAuthApiResponse(response);
    }
    return response.json();
  }

  // Reset password
  static async resetPassword(
    emailAddress: string,
    password: string,
    confirmPassword: string
  ) {
    const response = await fetch(`${baseUrl}auth/storefront/reset-password`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({ emailAddress, password, confirmPassword }),
    });
    if (!response.ok) {
      return await handleAuthApiResponse(response);
    }
    return response.json();
  }

  // Signup
  static async signup(user: AnnonymousUser) {
    const companyId = localStorage.getItem('companyId');
    const { emailAddress, firstName, lastName, phoneNumber } = user;
    return fetch(`${baseUrl}auth/storefront/customer`, {
      method: 'POST',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({
        emailAddress,
        firstName,
        lastName,
        phoneNumber,
        companyId: Number(companyId),
      }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        return error;
      });
  }

  // Update user details
  static async updateUserDetails(firstName: string, lastName: string) {
    const response = await fetch(`${baseUrl}auth/loggedin-customer`, {
      method: 'PUT',
      headers: AuthService.getAuthHeaders(),
      body: JSON.stringify({ firstName, lastName }),
    });
    if (!response.ok) {
      return await handleAuthApiResponse(response);
    }
    return response.json();
  }
}

export default AuthService;
